import { FC } from 'react';
import { RenderHTML } from '../renderHtml';
import { twMerge } from 'tailwind-merge';

export interface TextWithDropShadowColor {
  text: string;
  shadow: string;
}

interface Props {
  text: string;
  color?: TextWithDropShadowColor;
  displayShadow?: boolean;
  className?: string;
}

type TagProcessor = {
  pattern: RegExp;
  replacer: (match: string, value: string, content: string) => string;
};

const processMarkdown = (markdown: string): string => {
  const processors: TagProcessor[] = [
    {
      pattern: /\n\#[^#](.*)/g,
      replacer: (_, content) => `<h2>${processMarkdown(content)}</h2>`,
    },
    {
      pattern: /\n\#\#[^#](.*)/g,
      replacer: (_, content) => `<h3>${processMarkdown(content)}</h3>`,
    },
    {
      pattern: /\*\*((?:.(?!\*\*))*.?)\*\*/g,
      replacer: (_, content) => `<strong>${processMarkdown(content)}</strong>`,
    },
    {
      pattern: /\*((?:.(?!\*))*.?)\*/g,
      replacer: (_, content) => `<em>${processMarkdown(content)}</em>`,
    },
    {
      pattern: /\[color\]\(([^)]+)\)((?:.(?!\[color\]))*.)\[color\]/g,
      replacer: (_, className, content) =>
        `<span class="text-${className}">${processMarkdown(content)}</span>`,
    },
    {
      pattern: /\[weight\]\(([^)]+)\)((?:.(?!\[weight\]))*.)\[weight\]/g,
      replacer: (_, className, content) =>
        `<span class="font-${className}">${processMarkdown(content)}</span>`,
    },
    {
      pattern: /\[size\]\(([^)]+)\)((?:.(?!\[size\]))*.)\[size\]/g,
      replacer: (_, sizes, content) => {
        const sizeClasses = sizes
          .split('|')
          .map((size) => {
            if (size.includes(':')) {
              return size;
            }
            return `text-${size}`;
          })
          .join(' ');
        return `<span class="${sizeClasses}">${processMarkdown(content)}</span>`;
      },
    },
    {
      pattern: /\[cr\]/g,
      replacer: () => `<br />`,
    },
    {
      pattern: /^ {2,}(?=\S)/gm,
      replacer: () => `<br />`,
    },
    {
      pattern: /\[(.*?)\]\((.*?)\)/g,
      replacer: (_, text, link) => {
        if (link.startsWith('http')) {
          return `<a href="${link}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        }
        return `<Link href="${link}">${text}</Link>`;
      },
    },
  ];

  let result = markdown;
  for (const processor of processors) {
    result = result.replace(processor.pattern, processor.replacer);
  }
  return result;
};

// Extract plain text for shadow (removes all tags)
const getPlainText = (markdown: string): string => {
  return (
    markdown &&
    markdown
      .replace(/<h2>(.*?)<\/h2>/g, '$1')
      .replace(/<h3>(.*?)<\/h3>/g, '$1')
      .replace(/<strong>(.*?)<\/strong>/g, '$1')
      .replace(/<em>(.*?)<\/em>/g, '$1')
      .replace(/\[color\]\([^)]+\)((?:.(?!\[color\]))*.)\[color\]/g, '$1')
      .replace(/\[size\]\([^)]+\)((?:.(?!\[size\]))*.)\[size\]/g, '$1')
      .replace(/\[weight\]\([^)]+\)((?:.(?!\[weight\]))*.)\[weight\]/g, '$1')
      .replace(/\[cr\]/g, '')
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
  );
};

export const TextWithDropShadow: FC<Props> = ({
  text,
  color = undefined,
  displayShadow = true,
  className,
}) => {
  const htmlContent = text && processMarkdown(text);
  const plainTextForShadow = text && getPlainText(text);
  const sizeClasses = [];
  let sizeMatch;
  const sizeRegex = /\[size\]\(([^)]+)\)/g;

  const textColor: TextWithDropShadowColor = {
    text: (color && color.text) || 'text-black',
    shadow: (color && color.shadow) || 'text-black',
  };

  while ((sizeMatch = sizeRegex.exec(text)) !== null) {
    sizeClasses.push(sizeMatch[1]);
  }

  const shadowSizeClass = sizeClasses.join(' ');

  return (
    <div className={twMerge(className, 'relative inline-block')}>
      <RenderHTML
        className={twMerge('relative z-10', textColor.text)}
        data={htmlContent}
      />

      {displayShadow && (
        <span
          className={twMerge(
            'absolute inset-0 z-0',
            textColor.shadow,
            shadowSizeClass
          )}
          style={{ filter: 'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))' }}
        >
          <RenderHTML data={plainTextForShadow} />
        </span>
      )}
    </div>
  );
};
